import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Updater<T> = T | ((old: T) => T);

const applyUpdater = <T>(updater: Updater<T>, current: T): T =>
  typeof updater === "function" ? (updater as (old: T) => T)(current) : updater;

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((entry, index) => entry === b[index]);

/**
 * Reconcile a saved column order against the columns currently
 * available in the table:
 * - Drop any saved ids the table no longer knows about.
 * - Append any available ids the saved order doesn't mention.
 *
 * Used to keep stored orders forward-compatible with code that
 * adds, removes, or renames columns.
 */
export const reconcileColumnOrder = (
  savedColumnOrder: string[],
  availableColumnIds: string[],
): string[] => {
  const validSavedColumnOrder = savedColumnOrder.filter((columnId) =>
    availableColumnIds.includes(columnId),
  );
  const missingColumnIds = availableColumnIds.filter(
    (columnId) => !validSavedColumnOrder.includes(columnId),
  );
  return [...validSavedColumnOrder, ...missingColumnIds];
};

export interface UseReconciledColumnOrder {
  /**
   * Order to feed to MRT's `state.columnOrder` — the saved order
   * reconciled against `availableColumnIds`. Stable across renders
   * when the underlying data hasn't changed.
   */
  columnOrder: string[];
  /**
   * Apply a user-initiated reorder. Functional updaters are fed the
   * currently-displayed (reconciled) order so MRT's drag-reorder works
   * correctly even before any hydration has happened (when the raw
   * saved order is still `[]`). Calls the `onChange` option after
   * mutating state.
   */
  setColumnOrder: (updater: Updater<string[]>) => void;
  /**
   * Replace the saved order with a server-provided one. Skips work
   * when the incoming order matches what's already stored. Does NOT
   * call `onChange` — hydration is a server → client flow, the caller
   * doesn't want to round-trip the same value back.
   */
  hydrateColumnOrder: (serverOrder: string[]) => void;
  /**
   * Latest raw saved order (not reconciled). Exposed for callers that
   * build outgoing server payloads — they want exactly what was saved,
   * not the reconciled padding.
   */
  columnOrderRef: React.RefObject<string[]>;
}

interface UseReconciledColumnOrderOptions {
  /** Called after `setColumnOrder` mutates state. Use for server sync. */
  onChange?: () => void;
}

/**
 * Owns a saved column order plus the reconciliation logic that keeps
 * it valid as the table's available columns change. Designed to be
 * composed inside a larger view-state hook that also handles URL
 * params and server sync.
 *
 * @param availableColumnIds - ids of the columns the table currently
 *   knows about, in their default order.
 * @param options.onChange - fires after a user reorder. Hydration
 *   does not fire it.
 */
export function useReconciledColumnOrder(
  availableColumnIds: string[],
  options: UseReconciledColumnOrderOptions = {},
): UseReconciledColumnOrder {
  const [columnOrder, setColumnOrderState] = useState<string[]>([]);
  const columnOrderRef = useRef(columnOrder);

  const reconciledColumnOrder = useMemo(
    () => reconcileColumnOrder(columnOrder, availableColumnIds),
    [columnOrder, availableColumnIds],
  );
  // Tracks the order the table is _displaying_ (raw stored order
  // padded with any available column ids it doesn't yet know about).
  // This is what functional updaters from MRT expect as their input —
  // dragging a column onto the end of a six-column table needs to see
  // six entries, not the empty array we hold internally before any
  // reorder/hydration has happened.
  const reconciledColumnOrderRef = useRef(reconciledColumnOrder);
  const onChangeRef = useRef(options.onChange);
  useEffect(() => {
    columnOrderRef.current = columnOrder;
    reconciledColumnOrderRef.current = reconciledColumnOrder;
    onChangeRef.current = options.onChange;
  });

  const setColumnOrder = useCallback((updater: Updater<string[]>) => {
    const current = reconciledColumnOrderRef.current;
    const next = applyUpdater(updater, current);
    if (arraysEqual(next, current)) return;
    columnOrderRef.current = next;
    setColumnOrderState(next);
    onChangeRef.current?.();
  }, []);

  const hydrateColumnOrder = useCallback((serverOrder: string[]) => {
    if (arraysEqual(serverOrder, columnOrderRef.current)) return;
    columnOrderRef.current = serverOrder;
    setColumnOrderState(serverOrder);
  }, []);

  return {
    columnOrder: reconciledColumnOrder,
    setColumnOrder,
    hydrateColumnOrder,
    columnOrderRef,
  };
}
